/**
 * Query to determine if a Classroom is a Clever Library class
 */
import type * as React from "react";

import {FetchPolicy, type Result} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";

import type {getIsClassCleverLibrarySyncedType} from "./__graphql-types__/index.ts";
import {GET_IS_CLASS_CLEVER_LIBRARY_SYNCED_QUERY} from "./get-is-class-clever-library-synced.ts";

interface Props {
    classDescriptor?: string | null | undefined;
    children: (result: QueryResult) => React.ReactNode;
}

export interface QueryResult {
    error?: Error;
    isLoading: boolean;
    isCleverLibraryClass: boolean;
}

const mapDataToResult = (
    result: Result<getIsClassCleverLibrarySyncedType["response"]>,
): QueryResult => {
    return {
        error: result.status === "error" ? result.error : undefined,
        isLoading: result.status === "loading",
        isCleverLibraryClass: !!(
            result.status === "success" &&
            result.data &&
            result.data.classroom &&
            result.data.classroom.isCleverLibrarySynced
        ),
    };
};

export default function CleverLibraryClassQuery({
    classDescriptor,
    children,
}: Props) {
    const [result] = useQuery(GET_IS_CLASS_CLEVER_LIBRARY_SYNCED_QUERY, {
        variables: {classDescriptor: classDescriptor || ""},
        skip: !classDescriptor,
        fetchPolicy: FetchPolicy.CacheBeforeNetwork,
    });

    if (!classDescriptor) {
        return children({isCleverLibraryClass: false, isLoading: false});
    }

    return children(mapDataToResult(result));
}
